import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Coins, TrendingDown, Users, Loader2, Check, AlertTriangle, Sparkles, Zap, Building2 } from "lucide-react";

const PACK_META = {
  credits_100: { icon: Sparkles, accent: "text-secondary bg-secondary/15" },
  credits_500: { icon: Zap, accent: "text-primary bg-primary/10", popular: true },
  credits_1000: { icon: Building2, accent: "text-accent bg-accent/10" },
};

export default function Billing() {
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [buying, setBuying] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/billing/school");
      setData(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buy = async (lookup_key) => {
    setBuying(lookup_key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
      setBuying("");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>
      </div>
    );
  }

  const school = data?.school;
  const balance = school?.credit_balance ?? 0;
  const lowCredits = balance < 50;

  const stats = [
    { icon: Coins, label: t("billing_balance"), value: balance, cls: "text-primary bg-primary/10" },
    { icon: TrendingDown, label: t("billing_spent"), value: school?.credits_spent ?? 0, cls: "text-accent bg-accent/10" },
    { icon: Users, label: t("billing_parents"), value: data?.num_parents ?? 0, cls: "text-secondary bg-secondary/15" },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="mx-auto max-w-5xl px-5 py-12 lg:px-8">
        <div className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-accent">
          <Building2 className="h-4 w-4" /> {school?.name}
        </div>
        <h1 className="mt-2 font-heading text-3xl font-semibold tracking-tight text-foreground sm:text-4xl" data-testid="billing-title">
          {t("billing_title")}
        </h1>
        <p className="mt-2 max-w-2xl text-base text-muted-foreground">{t("billing_subtitle")}</p>

        {lowCredits && (
          <div data-testid="low-credits-warning" className="mt-6 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm font-medium text-amber-800">
            <AlertTriangle className="h-5 w-5 shrink-0" strokeWidth={2} />
            {t("low_credits_warning")}
          </div>
        )}

        <div className="mt-8 grid gap-4 sm:grid-cols-3" data-testid="billing-stats">
          {stats.map((s, i) => (
            <div key={i} className="rounded-3xl border border-black/5 bg-card p-6 shadow-sm">
              <span className={`grid h-11 w-11 place-items-center rounded-2xl ${s.cls}`}>
                <s.icon className="h-5 w-5" strokeWidth={1.75} />
              </span>
              <p className="mt-4 font-heading text-3xl font-semibold text-foreground" data-testid={`stat-${i}`}>{s.value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-foreground">{t("billing_buy_title")}</h2>
          <p className="mt-1 text-sm text-muted-foreground">{t("per_question")} · {t("per_helper")}</p>
          <div className="mt-6 grid gap-5 md:grid-cols-3" data-testid="credit-packs">
            {(data?.packs || []).map((pack) => {
              const meta = PACK_META[pack.lookup_key] || { icon: Sparkles, accent: "text-primary bg-primary/10" };
              const Icon = meta.icon;
              return (
                <div
                  key={pack.lookup_key}
                  data-testid={`pack-${pack.lookup_key}`}
                  className={`relative flex flex-col rounded-3xl border bg-card p-7 shadow-sm ${meta.popular ? "border-primary ring-2 ring-primary/20" : "border-black/5"}`}
                >
                  {meta.popular && (
                    <span className="absolute -top-3 left-7 rounded-full bg-primary px-3 py-1 text-xs font-semibold text-primary-foreground">
                      Popular
                    </span>
                  )}
                  <span className={`grid h-12 w-12 place-items-center rounded-2xl ${meta.accent}`}>
                    <Icon className="h-6 w-6" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-5 font-heading text-lg font-semibold text-foreground">{pack.name}</h3>
                  <p className="mt-1 font-heading text-4xl font-semibold text-foreground">
                    {pack.credits}
                    <span className="ml-1.5 text-base font-medium text-muted-foreground">{t("credits_word")}</span>
                  </p>
                  <p className="mt-2 text-sm text-muted-foreground">${pack.price_usd.toFixed(2)} USD</p>
                  <Button
                    data-testid={`buy-${pack.lookup_key}`}
                    onClick={() => buy(pack.lookup_key)}
                    disabled={!!buying}
                    className="mt-6 h-12 w-full rounded-full bg-primary font-semibold text-primary-foreground hover:bg-primary-hover"
                  >
                    {buying === pack.lookup_key ? <Loader2 className="h-5 w-5 animate-spin" /> : t("buy_now")}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>

        <div className="mt-12">
          <h2 className="font-heading text-2xl font-semibold text-foreground">{t("transactions_title")}</h2>
          {(!data?.transactions || data.transactions.length === 0) ? (
            <p data-testid="no-transactions" className="mt-4 rounded-2xl border border-dashed border-border bg-card px-5 py-8 text-center text-sm text-muted-foreground">
              {t("no_transactions")}
            </p>
          ) : (
            <div className="mt-5 overflow-hidden rounded-3xl border border-black/5 bg-card shadow-sm" data-testid="transactions-list">
              {data.transactions.map((tx, i) => (
                <div key={i} className="flex items-center justify-between border-b border-border/60 px-6 py-4 last:border-0">
                  <div>
                    <p className="font-semibold text-foreground">+{tx.credits} {t("credits_word")}</p>
                    <p className="text-xs text-muted-foreground">{new Date(tx.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm text-muted-foreground">${((tx.amount || 0) / 100).toFixed(2)}</span>
                    {tx.payment_status === "paid" ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-secondary/20 px-3 py-1 text-xs font-semibold text-secondary">
                        <Check className="h-3.5 w-3.5" strokeWidth={2.5} /> Paid
                      </span>
                    ) : (
                      <span className="rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700">{tx.payment_status}</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
}
